import { CheckCircle2, Clock, Truck, XCircle, type LucideIcon } from 'lucide-react';

export interface ReturnBox {
  delivered_at?: string | null;
  rto_received_at?: string | null;
}

export type BoxReturnState = 'delivered' | 'returned' | 'not_returned' | 'awaiting';

/** The box should already be back at Boxy's warehouse (F-MB-17 stamps every undelivered box on entry). */
const RETURN_DUE = [
  'rto-received-warehouse',
  'rto-scheduled',
  'rto-collecting',
  'rto-in-transit',
  'rto-drop-off-scanned',
  'rto-delivered',
  'rto-partially-delivered',
];

/** A return is on its way but not due yet — never red here. */
const RETURN_PENDING = [
  'rto-request-pending',
  'rto-requested',
  'rto-received-carrier-warehouse',
  'partially-delivered',
  'inventory',
];

export const isReturnLeg = (statusSlug?: string) =>
  RETURN_DUE.includes(statusSlug ?? '') || RETURN_PENDING.includes(statusSlug ?? '');

/**
 * BOXY-556 — where one box stands on the return leg; null on a forward status.
 * A box delivered to the customer is not coming back, so it is never red.
 */
export function boxReturnState(box: ReturnBox, statusSlug?: string): BoxReturnState | null {
  if (!isReturnLeg(statusSlug)) return null;
  if (box.delivered_at) return 'delivered';
  if (box.rto_received_at) return 'returned';
  return RETURN_DUE.includes(statusSlug ?? '') ? 'not_returned' : 'awaiting';
}

/** Returned X of Y — Y counts only the boxes that can come back (core's X−N). */
export const returnTally = (boxes: ReturnBox[]) => {
  const returnable = boxes.filter((box) => !box.delivered_at);
  return {
    returned: returnable.filter((box) => box.rto_received_at).length,
    count: returnable.length,
  };
};

export const RETURN_LINE: Record<
  BoxReturnState,
  { Icon: LucideIcon; labelKey: string; tone: string; at: keyof ReturnBox | null }
> = {
  delivered: {
    Icon: Truck,
    labelKey: 'box_status_delivered_to_customer',
    tone: 'text-sd-muted-foreground',
    at: 'delivered_at',
  },
  returned: {
    Icon: CheckCircle2,
    labelKey: 'box_status_returned',
    tone: 'text-sd-dest-green',
    at: 'rto_received_at',
  },
  not_returned: {
    Icon: XCircle,
    labelKey: 'box_status_not_returned',
    tone: 'text-sd-destructive font-medium',
    at: null,
  },
  awaiting: {
    Icon: Clock,
    labelKey: 'box_status_awaiting_return',
    tone: 'text-sd-muted-foreground',
    at: null,
  },
};

/**
 * The red row: tint + a start-side bar (right in RTL). The bar is a pseudo
 * element, not a border: utilities compile `!important` here, so the list's
 * `divide-sd-input` (more specific) would recolour any border after row 1.
 */
export const NOT_RETURNED_ROW =
  'relative bg-sd-destructive/10 before:absolute before:inset-y-0 before:start-0 before:w-0.5 before:bg-sd-destructive';
